import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { X } from "lucide-react";

/** Compact, dismissible newsletter invitation. Shown after a visitor has
 * clicked through to donate and returned — never claims payment completed. */
export default function NewsletterPopup() {
  const [visible, setVisible] = useState(() => {
    try {
      return (
        localStorage.getItem("mmr_donation_click") &&
        !localStorage.getItem("mmr_newsletter_done")
      );
    } catch { return false; }
  });
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem("mmr_newsletter_done", "1"); } catch {}
    setVisible(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      await api.post("/newsletter/subscribe", { email, website: "" });
      setState({
        status: "success",
        message: "Thank you. Please check your inbox for a confirmation link to activate your subscription.",
      });
      try { localStorage.setItem("mmr_newsletter_done", "1"); } catch {}
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-popup-heading"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-charcoal/40"
      data-testid="newsletter-popup"
    >
      <div className="relative bg-white border border-sandstone shadow-lg shadow-black/10 max-w-md w-full p-8">
        <button
          onClick={dismiss}
          aria-label="Close"
          data-testid="newsletter-popup-close"
          className="absolute top-3 right-3 p-2 text-charcoal/60 hover:text-charcoal transition-colors duration-300"
        >
          <X size={18} />
        </button>
        <h2 id="newsletter-popup-heading" className="font-serif text-2xl sm:text-3xl font-semibold text-forest leading-snug">
          Thank You for Supporting Our Mission
        </h2>
        <p className="mt-3 text-sm text-charcoal/80 leading-relaxed">
          Stay connected with Mount Moriah Relief. Receive updates about our work,
          community projects and the difference your support makes.
        </p>
        {state.status === "success" ? (
          <p className="mt-5 text-sm text-forest font-medium" data-testid="newsletter-popup-success">
            {state.message}
          </p>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <label htmlFor="popup-email" className="sr-only">Enter your email address</label>
            <input
              id="popup-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              data-testid="newsletter-popup-email-input"
              className="w-full border border-sage bg-transparent px-4 py-3 text-sm focus:border-forest focus:ring-1 focus:ring-forest rounded-sm"
            />
            {state.status === "error" && (
              <p className="text-sm text-red-700" data-testid="newsletter-popup-error">{state.message}</p>
            )}
            <button
              type="submit"
              disabled={state.status === "loading"}
              data-testid="newsletter-popup-subscribe-button"
              className="w-full bg-forest text-white font-medium px-6 py-3 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60"
            >
              {state.status === "loading" ? "Subscribing…" : "Subscribe to Updates"}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="newsletter-popup-later-button"
              className="w-full text-sm text-charcoal/70 hover:text-charcoal py-1 transition-colors duration-300"
            >
              Maybe Later
            </button>
          </form>
        )}
        <p className="mt-4 text-xs text-charcoal/60 leading-relaxed">
          We will only email you occasional updates. You can unsubscribe at any time
          using the link in every email. No account or password is needed.
        </p>
      </div>
    </div>
  );
}
